import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../config';
import { IconPhone } from '../components/Icons';
import AgentToggle from '../components/AgentToggle';

const nf = (n) => (n == null ? '—' : Number(n).toLocaleString('it-IT'));
const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);

/* ── primitivi UI (token, tema-adattivi) ── */
function Card({ title, right, children, style }) {
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 16, padding: 18, boxShadow: '0 1px 2px var(--card-shadow)', display: 'flex', flexDirection: 'column', minWidth: 0, ...style }}>
      {(title || right) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          {title && <h3 style={{ margin: 0, fontSize: 14.5, fontWeight: 700 }}>{title}</h3>}
          <div style={{ flex: 1 }} />
          {right}
        </div>
      )}
      {children}
    </div>
  );
}

function Kpi({ label, value, sub, color, accentBar = true }) {
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderLeft: accentBar ? `4px solid ${color || 'var(--accent)'}` : undefined, borderRadius: 14, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
      <span style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 600 }}>{label}</span>
      <span className="num" style={{ fontSize: 25, fontWeight: 800, letterSpacing: '-.6px', lineHeight: 1, color: color || 'var(--text)' }}>{value}</span>
      {sub && <span style={{ fontSize: 11.5, color: 'var(--faint)', fontWeight: 600 }}>{sub}</span>}
    </div>
  );
}

/* Donut via conic-gradient (niente librerie) */
function Donut({ segments, center }) {
  const tot = segments.reduce((s, x) => s + x.value, 0) || 1;
  let acc = 0;
  const stops = segments.map((s) => {
    const from = (acc / tot) * 100; acc += s.value; const to = (acc / tot) * 100;
    return `${s.color} ${from}% ${to}%`;
  }).join(', ');
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
      <div className="chart-in" style={{ width: 118, height: 118, borderRadius: '50%', flex: '0 0 auto', background: `conic-gradient(${stops || 'var(--surface-2) 0 100%'})`, display: 'grid', placeItems: 'center' }}>
        <div style={{ width: 82, height: 82, borderRadius: '50%', background: 'var(--surface)', display: 'grid', placeItems: 'center', textAlign: 'center' }}>
          {center}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, flex: 1, minWidth: 120 }}>
        {segments.map((s) => (
          <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--muted)' }}>
            <span style={{ width: 9, height: 9, borderRadius: 3, background: s.color }} />
            {s.label}
            <b className="num" style={{ marginLeft: 'auto', color: 'var(--text)' }}>{nf(s.value)}</b>
            <span className="num" style={{ color: 'var(--faint)', width: 40, textAlign: 'right' }}>{pct(s.value, tot)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* Grafico a barre impilate (NON TIM + TIM) per giorno — SVG, token */
function AndamentoChart({ serie }) {
  if (!serie || serie.length === 0) return <div style={{ color: 'var(--faint)', fontSize: 13, padding: 16 }}>Nessun dato nel periodo.</div>;
  const max = Math.max(1, ...serie.map((d) => d.tot));
  const W = 640, H = 150, pad = 4;
  const n = serie.length;
  const bw = (W - pad * (n - 1)) / n;
  return (
    <div style={{ overflowX: 'auto' }}>
      <svg viewBox={`0 0 ${W} ${H + 20}`} width="100%" height={H + 20} preserveAspectRatio="none" style={{ minWidth: 420 }}>
        {[0.25, 0.5, 0.75, 1].map((g) => (
          <line key={g} x1="0" x2={W} y1={H - H * g} y2={H - H * g} stroke="var(--line)" strokeWidth="1" />
        ))}
        {serie.map((d, i) => {
          const x = i * (bw + pad);
          const hN = (d.non_tim / max) * H;
          const hT = (d.tim / max) * H;
          const hOther = ((d.tot - d.non_tim - d.tim) / max) * H;
          let y = H;
          const rects = [];
          [['var(--ok)', hN], ['var(--err)', hT], ['var(--warn)', hOther]].forEach(([c, h], k) => {
            if (h > 0) { y -= h; rects.push(<rect key={k} className="grow-y" style={{ animationDelay: `${i * 18}ms` }} x={x} y={y} width={bw} height={h} fill={c} rx="2" />); }
          });
          return (
            <g key={i}>
              {rects}
              <title>{`${d.data}\nTot ${d.tot} · NON TIM ${d.non_tim} · TIM ${d.tim}`}</title>
              {(i % Math.ceil(n / 8) === 0) && (
                <text x={x + bw / 2} y={H + 14} fontSize="9" fill="var(--faint)" textAnchor="middle">{d.data.slice(5)}</text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function Bars({ rows, max, color }) {
  const m = max || Math.max(1, ...rows.map((r) => r.value));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {rows.map((r) => (
        <div key={r.label} style={{ display: 'grid', gridTemplateColumns: '84px 1fr 46px', alignItems: 'center', gap: 10, fontSize: 12.5 }}>
          <span style={{ color: 'var(--muted)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.label}</span>
          <span style={{ height: 8, background: 'var(--surface-2)', borderRadius: 5, overflow: 'hidden' }}>
            <span className="grow-x" style={{ display: 'block', height: '100%', width: `${pct(r.value, m)}%`, background: color || 'var(--accent)', borderRadius: 5 }} />
          </span>
          <b className="num" style={{ textAlign: 'right' }}>{nf(r.value)}</b>
        </div>
      ))}
    </div>
  );
}

const SEG = [7, 14, 30];

export default function Cruscotto456Page() {
  const [ag, setAg] = useState(null);
  const [vs, setVs] = useState(null);
  const [lead, setLead] = useState(0);
  const [giorni, setGiorni] = useState(14);
  const [and, setAnd] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/agent/status`).then((r) => setAg(r.data)).catch(() => {});
    axios.get(`${API}/verify-stats`).then((r) => setVs(r.data)).catch(() => {});
    axios.get(`${API}/numeri?lead=true&limit=1`).then((r) => setLead(r.data?.total ?? 0)).catch(() => {});
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);
  useEffect(() => { axios.get(`${API}/verify-stats/andamento?giorni=${giorni}`).then((r) => setAnd(r.data?.serie || [])).catch(() => {}); }, [giorni]);

  const attivo = !!ag?.attivo;
  const inAttesa = !!ag?.in_attesa;
  const standby = ag?.job?.status === 'standby';
  const tot = vs?.totale_verificati || 0;
  const resa = vs?.pct_non_tim ?? 0;
  const perLine = ag?.per_line || [];
  const province = (vs?.province || []).slice(0, 8).map((p) => ({ label: p._id || '—', value: p.non_tim }));

  const statoTxt = standby ? 'In standby (disservizio)' : attivo ? (inAttesa ? 'In attesa (coda vuota)' : 'In chiamata') : 'Fermo';
  const statoColor = standby ? 'var(--warn)' : attivo ? (inAttesa ? 'var(--info)' : 'var(--ok)') : 'var(--faint)';

  return (
    <div style={{ padding: 20, maxWidth: 1760, margin: '0 auto' }}>
      {/* header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 18 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 21, fontWeight: 800, letterSpacing: '-.4px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <IconPhone size={20} color="var(--accent)" /> Cruscotto 456
          </h1>
          <p style={{ margin: '3px 0 0', color: 'var(--muted)', fontSize: 12.5 }}>Verifica TIM / NON TIM via servizio 456 — statistiche e controllo agente.</p>
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: '8px 12px' }}>
          <span style={{ width: 9, height: 9, borderRadius: '50%', background: statoColor }} />
          <b style={{ fontSize: 13 }}>{statoTxt}</b>
          {attivo && !standby && <span className="num" style={{ color: 'var(--muted)', fontSize: 12.5 }}>~{nf(ag?.velocita_min)}/min</span>}
        </div>
        <AgentToggle />
      </div>

      {/* KPI */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 14, marginBottom: 16 }}>
        <Kpi label="Verificati" value={nf(tot)} sub={`${nf(vs?.verifiche_24h)} nelle 24h`} color="var(--accent)" />
        <Kpi label="TIM" value={nf(vs?.tim)} sub={`${vs?.pct_tim ?? 0}%`} color="var(--err)" />
        <Kpi label="NON TIM" value={nf(vs?.non_tim)} sub={`${vs?.pct_non_tim ?? 0}% · lead`} color="var(--ok)" />
        <Kpi label="Errore" value={nf(vs?.errore)} sub={`${vs?.pct_errore ?? 0}%`} color="var(--warn)" />
        <Kpi label="Resa" value={`${resa}%`} sub="NON TIM / verificati" color="var(--ok)" />
        <Kpi label="Verifiche / ora" value={nf(vs?.velocita_media_ora)} sub="media 24h" color="var(--info)" />
      </div>

      {/* andamento + donut */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.7fr 1fr', gap: 16, marginBottom: 16, alignItems: 'start' }}>
        <Card title="Andamento verifiche"
          right={
            <div style={{ display: 'inline-flex', gap: 3, background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 9, padding: 3 }}>
              {SEG.map((g) => (
                <button key={g} onClick={() => setGiorni(g)} style={{ border: 'none', cursor: 'pointer', borderRadius: 7, padding: '5px 11px', fontSize: 12, fontWeight: 700, background: giorni === g ? 'var(--accent)' : 'transparent', color: giorni === g ? 'var(--on-accent)' : 'var(--muted)' }}>{g}g</button>
              ))}
            </div>
          }>
          <AndamentoChart serie={and} />
          <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: 11.5, color: 'var(--muted)' }}>
            <span><span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 3, background: 'var(--ok)', marginRight: 5 }} />NON TIM</span>
            <span><span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 3, background: 'var(--err)', marginRight: 5 }} />TIM</span>
            <span><span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 3, background: 'var(--warn)', marginRight: 5 }} />Errore</span>
          </div>
        </Card>
        <Card title="Ripartizione esiti">
          <Donut
            segments={[
              { label: 'NON TIM', value: vs?.non_tim || 0, color: 'var(--ok)' },
              { label: 'TIM', value: vs?.tim || 0, color: 'var(--err)' },
              { label: 'Errore', value: vs?.errore || 0, color: 'var(--warn)' },
            ]}
            center={<><b className="num" style={{ fontSize: 20, fontWeight: 800 }}>{resa}%</b><small style={{ display: 'block', fontSize: 10.5, color: 'var(--faint)', fontWeight: 600 }}>resa</small></>}
          />
        </Card>
      </div>

      {/* linee SIP + velocità/coda */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.7fr 1fr', gap: 16, marginBottom: 16, alignItems: 'start' }}>
        <Card title="Linee SIP" right={<span style={{ fontSize: 12, color: 'var(--faint)' }}>{perLine.length} linee</span>}>
          {perLine.length === 0 ? (
            <div style={{ color: 'var(--faint)', fontSize: 13 }}>Nessuna linea configurata o agente fermo.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {perLine.map((l) => {
                const done = l.completati || 0;
                const tt = (ag?.job?.total) || 0;
                return (
                  <div key={l.server || l.nome} style={{ background: 'var(--surface-2)', borderRadius: 12, padding: '11px 13px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: attivo ? 'var(--ok)' : 'var(--faint)' }} />
                      <b style={{ fontSize: 13 }}>{l.nome || l.server}</b>
                      <div style={{ flex: 1 }} />
                      <span className="num" style={{ fontSize: 12, color: 'var(--muted)' }}>{l.velocita_media_min != null ? `${nf(l.velocita_media_min)}/min` : '—'} · {nf(done)} fatti</span>
                    </div>
                    <div style={{ height: 7, background: 'var(--surface)', borderRadius: 5, overflow: 'hidden' }}>
                      <div className="grow-x" style={{ height: '100%', width: `${pct(done, tt || done || 1)}%`, background: 'linear-gradient(90deg,var(--accent),var(--accent-dark))', borderRadius: 5, transition: 'width .5s' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
        <Card title="Velocità e coda">
          <div style={{ display: 'flex', gap: 18, marginBottom: 14 }}>
            <div><div className="num" style={{ fontSize: 22, fontWeight: 800, color: 'var(--warn)' }}>{nf(ag?.pending)}</div><div style={{ fontSize: 11, color: 'var(--muted)' }}>Da fare (coda)</div></div>
            <div><div className="num" style={{ fontSize: 22, fontWeight: 800 }}>{attivo ? nf(ag?.velocita_min) : '—'}</div><div style={{ fontSize: 11, color: 'var(--muted)' }}>al minuto</div></div>
            <div><div className="num" style={{ fontSize: 22, fontWeight: 800, color: 'var(--err)' }}>{nf(vs?.totale_disservizi)}</div><div style={{ fontSize: 11, color: 'var(--muted)' }}>disservizi</div></div>
          </div>
          <div style={{ borderTop: '1px solid var(--line)', paddingTop: 10 }}>
            <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--muted)', marginBottom: 6 }}>Velocità (storico)</div>
            {[['24h', 'h24'], ['7 giorni', 'g7'], ['Sempre', 'sempre']].map(([label, key]) => {
              const s = (vs?.velocita_storica || {})[key] || {};
              return (
                <div key={key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, padding: '3px 0' }}>
                  <span style={{ color: 'var(--muted)' }}>{label}</span>
                  <span className="num"><b>{s.vel_min != null ? nf(s.vel_min) : '—'}</b>/min · {nf(s.verifiche || 0)}</span>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* province */}
      <Card title="Top province — lead NON TIM" right={<span style={{ fontSize: 12, color: 'var(--faint)' }}>Lead fibra totali: <b style={{ color: 'var(--info)' }}>{nf(lead)}</b></span>}>
        {province.length === 0 ? (
          <div style={{ color: 'var(--faint)', fontSize: 13 }}>Nessun dato per provincia.</div>
        ) : <Bars rows={province} color="var(--ok)" />}
      </Card>
    </div>
  );
}
